
<template>
  <section class="calculator-module">
    <div class="module-header">
      <h2>{{ t('parAnalyzer.title').value }}</h2>
      <span class="toggle-icon">▼</span>
    </div>
    <div class="module-content">
      <div class="calculator-intro">
        <h3>{{ t('parAnalyzer.subtitle').value }}</h3>
        <p>{{ t('parAnalyzer.description').value }}</p>
      </div>
      
      <div class="calculator-tabs">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'calculator' }"
          @click="activeTab = 'calculator'"
        >
          {{ t('parAnalyzer.tabs.calculator').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'chart' }"
          @click="activeTab = 'chart'"
        >
          {{ t('parAnalyzer.tabs.chart').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'heatmap' }"
          @click="activeTab = 'heatmap'"
        >
          {{ t('parAnalyzer.tabs.heatmap').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'recommendations' }"
          @click="activeTab = 'recommendations'"
        >
          {{ t('parAnalyzer.tabs.recommendations').value }}
        </button>
      </div>
      
      <!-- PAR计算 -->
      <div v-if="activeTab === 'calculator'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('parAnalyzer.sections.lightParams').value }}</h4>
          <div class="form-group">
            <label for="light_type">{{ t('parAnalyzer.labels.lightType').value }}</label>
            <select
              id="light_type"
              v-model="parData.lightType"
              class="select-control"
              @change="onLightTypeChange"
            >
              <option value="led">{{ t('parAnalyzer.options.led').value }}</option>
              <option value="t5">{{ t('parAnalyzer.options.t5').value }}</option>
              <option value="metal_halide">{{ t('parAnalyzer.options.metalHalide').value }}</option>
            </select>
            <div class="light-type-tips">
              <div v-if="parData.lightType === 'led'" class="tip-content">
                <strong>{{ t('parAnalyzer.tips.led.advantages').value }}：</strong>{{ t('parAnalyzer.tips.led.advantagesText').value }}<br>
                <strong>{{ t('parAnalyzer.tips.led.notice').value }}：</strong>{{ t('parAnalyzer.tips.led.noticeText').value }}<br>
                <strong>{{ t('parAnalyzer.tips.led.fluorescence').value }}：</strong>{{ t('parAnalyzer.tips.led.fluorescenceText').value }}
              </div>
              <div v-if="parData.lightType === 'metal_halide'" class="tip-content">
                <strong>{{ t('parAnalyzer.tips.metalHalide.advantages').value }}：</strong>{{ t('parAnalyzer.tips.metalHalide.advantagesText').value }}<br>
                <strong>{{ t('parAnalyzer.tips.metalHalide.disadvantages').value }}：</strong>{{ t('parAnalyzer.tips.metalHalide.disadvantagesText').value }}<br>
                <strong>{{ t('parAnalyzer.tips.metalHalide.notice').value }}：</strong>{{ t('parAnalyzer.tips.metalHalide.noticeText').value }}
              </div>
              <div v-if="parData.lightType === 't5'" class="tip-content">
                <strong>{{ t('parAnalyzer.tips.t5.advantages').value }}：</strong>{{ t('parAnalyzer.tips.t5.advantagesText').value }}<br>
                <strong>{{ t('parAnalyzer.tips.t5.disadvantages').value }}：</strong>{{ t('parAnalyzer.tips.t5.disadvantagesText').value }}
              </div>
            </div>
          </div>
          
          <div v-if="parData.lightType === 'led'" class="form-group">
            <label for="led_brand">{{ t('parAnalyzer.labels.ledBrand').value }}</label>
            <select
              id="led_brand"
              v-model="parData.ledBrand"
              class="select-control"
              @change="onLedBrandChange"
            >
              <option value="custom">{{ t('parAnalyzer.options.customLed').value }}</option>
              <option value="aipai_a8_pro">{{ t('parAnalyzer.options.aipaiA8Pro').value }}</option>
              <option value="aipai_a7_3rd">{{ t('parAnalyzer.options.aipaiA73rd').value }}</option>
              <option value="kessil_a360x">{{ t('parAnalyzer.options.kessilA360x').value }}</option>
              <option value="ai_prime_hd">{{ t('parAnalyzer.options.aiPrimeHd').value }}</option>
              <option value="radion_xr15">{{ t('parAnalyzer.options.radionXr15').value }}</option>
              <option value="radion_xr30">{{ t('parAnalyzer.options.radionXr30').value }}</option>
            </select>
          </div>
          
          <div v-if="parData.lightType === 'led'" class="form-group">
            <label for="light_power_percent">{{ t('parAnalyzer.labels.lightPowerPercent').value }}</label>
            <input
              type="range"
              id="light_power_percent"
              v-model="parData.lightPowerPercent"
              class="form-control-range"
              min="0"
              max="100"
              step="1"
            >
            <div class="power-display">{{ parData.lightPowerPercent }}%</div>
          </div>
          
          <div class="form-group">
            <label for="light_power">{{ t('parAnalyzer.labels.lightPower').value }}</label>
            <input
              type="number"
              id="light_power"
              v-model="parData.lightPower"
              class="form-control"
              :placeholder="t('parAnalyzer.placeholders.lightPower').value"
              step="1"
              min="10"
              max="1000"
            >
          </div>
          
          <div class="form-group">
            <label for="light_height">{{ t('parAnalyzer.labels.lightHeight').value }}</label>
            <input
              type="number"
              id="light_height"
              v-model="parData.lightHeight"
              class="form-control"
              :placeholder="t('parAnalyzer.placeholders.lightHeight').value"
              step="1"
              min="10"
              max="100"
            >
          </div>
          
          <div class="form-group">
            <label for="tank_depth">{{ t('parAnalyzer.labels.tankDepth').value }}</label>
            <input
              type="number"
              id="tank_depth"
              v-model="parData.tankDepth"
              class="form-control"
              :placeholder="t('parAnalyzer.placeholders.tankDepth').value"
              step="1"
              min="20"
              max="100"
            >
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('parAnalyzer.sections.measurementParams').value }}</h4>
          <div class="form-group">
            <label for="measurement_depth">{{ t('parAnalyzer.labels.measurementDepth').value }}</label>
            <input
              type="number"
              id="measurement_depth"
              v-model="parData.measurementDepth"
              class="form-control"
              :placeholder="t('parAnalyzer.placeholders.measurementDepth').value"
              step="1"
              min="0"
              max="100"
            >
          </div>
          
          <div class="form-group">
            <label for="actual_par">{{ t('parAnalyzer.labels.actualPar').value }}</label>
            <input
              type="number"
              id="actual_par"
              v-model="parData.actualPar"
              class="form-control"
              :placeholder="t('parAnalyzer.placeholders.actualPar').value"
              step="1"
              min="0"
              max="1000"
            >
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="calculatePAR"
          :disabled="!canCalculatePAR"
        >
          📊 {{ t('parAnalyzer.buttons.calculate').value }}
        </button>
      </div>
      
      <!-- 光照建议 -->
      <div v-if="activeTab === 'recommendations'" class="recommendations-section">
        <div class="recommendations-grid">
          <div class="recommendation-card" :class="getCoralClass('soft')">
            <h4>{{ t('parAnalyzer.coral.soft.title').value }}</h4>
            <div class="par-range">
              <span class="range-label">{{ t('parAnalyzer.coral.recommendedPar').value }}:</span>
              <span class="range-value">50-150</span>
            </div>
            <div class="coral-examples">
              <p><strong>{{ t('parAnalyzer.coral.commonSpecies').value }}:</strong></p>
              <ul>
                <li>{{ t('parAnalyzer.coral.soft.species1').value }}</li>
                <li>{{ t('parAnalyzer.coral.soft.species2').value }}</li>
                <li>{{ t('parAnalyzer.coral.soft.species3').value }}</li>
                <li>{{ t('parAnalyzer.coral.soft.species4').value }}</li>
              </ul>
            </div>
            <div class="lighting-tips">
              <p><strong>{{ t('parAnalyzer.coral.lightingTips').value }}:</strong></p>
              <ul>
                <li>{{ t('parAnalyzer.coral.soft.tip1').value }}</li>
                <li>{{ t('parAnalyzer.coral.soft.tip2').value }}</li>
                <li>{{ t('parAnalyzer.coral.soft.tip3').value }}</li>
              </ul>
            </div>
          </div>
          
          <div class="recommendation-card" :class="getCoralClass('lps')">
            <h4>{{ t('parAnalyzer.coral.lps.title').value }}</h4>
            <div class="par-range">
              <span class="range-label">{{ t('parAnalyzer.coral.recommendedPar').value }}:</span>
              <span class="range-value">100-250</span>
            </div>
            <div class="coral-examples">
              <p><strong>{{ t('parAnalyzer.coral.commonSpecies').value }}:</strong></p>
              <ul>
                <li>{{ t('parAnalyzer.coral.lps.species1').value }}</li>
                <li>{{ t('parAnalyzer.coral.lps.species2').value }}</li>
                <li>{{ t('parAnalyzer.coral.lps.species3').value }}</li>
                <li>{{ t('parAnalyzer.coral.lps.species4').value }}</li>
              </ul>
            </div>
            <div class="lighting-tips">
              <p><strong>{{ t('parAnalyzer.coral.lightingTips').value }}:</strong></p>
              <ul>
                <li>{{ t('parAnalyzer.coral.lps.tip1').value }}</li>
                <li>{{ t('parAnalyzer.coral.lps.tip2').value }}</li>
                <li>{{ t('parAnalyzer.coral.lps.tip3').value }}</li>
              </ul>
            </div>
          </div>
          
          <div class="recommendation-card" :class="getCoralClass('sps')">
            <h4>{{ t('parAnalyzer.coral.sps.title').value }}</h4>
            <div class="par-range">
              <span class="range-label">{{ t('parAnalyzer.coral.recommendedPar').value }}:</span>
              <span class="range-value">200-450</span>
            </div>
            <div class="coral-examples">
              <p><strong>{{ t('parAnalyzer.coral.commonSpecies').value }}:</strong></p>
              <ul>
                <li>{{ t('parAnalyzer.coral.sps.species1').value }}</li>
                <li>{{ t('parAnalyzer.coral.sps.species2').value }}</li>
                <li>{{ t('parAnalyzer.coral.sps.species3').value }}</li>
                <li>{{ t('parAnalyzer.coral.sps.species4').value }}</li>
              </ul>
            </div>
            <div class="lighting-tips">
              <p><strong>{{ t('parAnalyzer.coral.lightingTips').value }}:</strong></p>
              <ul>
                <li>{{ t('parAnalyzer.coral.sps.tip1').value }}</li>
                <li>{{ t('parAnalyzer.coral.sps.tip2').value }}</li>
                <li>{{ t('parAnalyzer.coral.sps.tip3').value }}</li>
              </ul>
            </div>
          </div>
          
        </div>
        
        <div class="general-tips">
          <h4>💡 {{ t('parAnalyzer.generalTips.title').value }}</h4>
          <div class="tips-grid">
            <div class="tip-item">
              <h5>{{ t('parAnalyzer.generalTips.lightingTime.title').value }}</h5>
              <p>{{ t('parAnalyzer.generalTips.lightingTime.content').value }}</p>
            </div>
            <div class="tip-item">
              <h5>{{ t('parAnalyzer.generalTips.spectrumBalance.title').value }}</h5>
              <p>{{ t('parAnalyzer.generalTips.spectrumBalance.content').value }}</p>
            </div>
            <div class="tip-item">
              <h5>{{ t('parAnalyzer.generalTips.lightingAdaptation.title').value }}</h5>
              <p>{{ t('parAnalyzer.generalTips.lightingAdaptation.content').value }}</p>
            </div>
            <div class="tip-item">
              <h5>{{ t('parAnalyzer.generalTips.regularMeasurement.title').value }}</h5>
              <p>{{ t('parAnalyzer.generalTips.regularMeasurement.content').value }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 深度图表 -->
      <div v-if="activeTab === 'chart'" class="chart-section">
        <div v-if="!parResults.show" class="no-data-message">
          <p>{{ t('parAnalyzer.chart.noDataMessage').value }}</p>
          <button class="calculate-btn" @click="activeTab = 'calculator'">
            {{ t('parAnalyzer.chart.goToCalculation').value }}
          </button>
        </div>
        <div v-else>
        <div class="chart-container">
          <div class="chart-header">
            <h4>{{ t('parAnalyzer.chart.title').value }}</h4>
            <div class="chart-legend">
              <div class="legend-item">
                <span class="legend-color soft"></span>
                <span>{{ t('parAnalyzer.chart.legend.soft').value }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-color lps"></span>
                <span>{{ t('parAnalyzer.chart.legend.lps').value }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-color sps"></span>
                <span>{{ t('parAnalyzer.chart.legend.sps').value }}</span>
              </div>
            </div>
          </div>
          
          <div class="depth-chart">
            <div class="par-scale">
              <div class="par-mark" v-for="parValue in parMarks" :key="parValue">
                <span class="par-value-label">{{ parValue }} PAR</span>
                <div class="par-line"></div>
              </div>
            </div>
            
            <div class="par-bars">
              <div
                v-for="(bar, index) in parBars"
                :key="index"
                class="par-bar-container"
              >
                <div
                  class="par-bar"
                  :class="getBarClass(bar.par)"
                  :style="{
                    height: bar.height + '%'
                  }"
                >
                  <div class="par-value">{{ bar.par }}</div>
                  <div class="par-label">PAR</div>
                </div>
                <div class="depth-label">{{ bar.depthLabel }}</div>
              </div>
            </div>
            
            <div class="coral-zones">
              <div
                class="coral-zone sps"
                :style="getCoralZoneStyle('sps')"
              >
              </div>
              <div
                class="coral-zone lps"
                :style="getCoralZoneStyle('lps')"
              >
              </div>
              <div
                class="coral-zone soft"
                :style="getCoralZoneStyle('soft')"
              >
              </div>
              <div
                class="coral-zone deadzone"
                :style="getCoralZoneStyle('deadzone')"
              >
              </div>
            </div>
          </div>
        </div>
        
        <div class="chart-notes">
          <h5>📝 {{ t('parAnalyzer.chart.notes.title').value }}</h5>
          <ul>
            <li>{{ t('parAnalyzer.chart.notes.note1').value }}</li>
            <li>{{ t('parAnalyzer.chart.notes.note2').value }}</li>
            <li>{{ t('parAnalyzer.chart.notes.note3').value }}</li>
            <li>{{ t('parAnalyzer.chart.notes.note4').value }}</li>
          </ul>
        </div>
        </div>
      </div>
      
      <!-- 热力图 -->
      <div v-if="activeTab === 'heatmap'" class="heatmap-section">
        <div v-if="!parResults.show" class="no-data-message">
          <p>{{ t('parAnalyzer.heatmap.noDataMessage').value }}</p>
          <button class="calculate-btn" @click="activeTab = 'calculator'">
            {{ t('parAnalyzer.heatmap.goToCalculation').value }}
          </button>
        </div>
        <div v-else>
        <div class="heatmap-container">
          <div class="heatmap-header">
            <h4>{{ t('parAnalyzer.heatmap.title').value }}</h4>
            <div class="heatmap-legend">
              <div class="legend-item">
                <span class="legend-color" style="background: #3498db"></span>
                <span>{{ t('parAnalyzer.heatmap.legend.low').value }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-color" style="background: #2ecc71"></span>
                <span>{{ t('parAnalyzer.heatmap.legend.mediumLow').value }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-color" style="background: #f39c12"></span>
                <span>{{ t('parAnalyzer.heatmap.legend.medium').value }}</span>
              </div>
              <div class="legend-item">
                <span class="legend-color" style="background: #e74c3c"></span>
                <span>{{ t('parAnalyzer.heatmap.legend.high').value }}</span>
              </div>
            </div>
          </div>
          
          <div class="heatmap-grid">
            <div class="heatmap-view">
              <h5>{{ t('parAnalyzer.heatmap.topView.title').value }} ({{ t('parAnalyzer.heatmap.topView.measurementDepth').value }}: {{ parData.measurementDepth }}cm)</h5>
              <div class="heatmap">
                <div
                  v-for="(row, rowIndex) in heatmapData.topView"
                  :key="rowIndex"
                  class="heatmap-row"
                >
                  <div
                    v-for="(cell, cellIndex) in row"
                    :key="cellIndex"
                    class="heatmap-cell"
                    :style="{
                      backgroundColor: getHeatmapColor(cell),
                      color: getHeatmapTextColor(cell, heatmapData.maxPar)
                    }"
                  >
                    {{ cell }}
                  </div>
                </div>
              </div>
              <div class="heatmap-labels">
                <div class="label">{{ t('parAnalyzer.heatmap.topView.lightPosition').value }}</div>
              </div>
            </div>
            
            <div class="heatmap-view">
              <h5>{{ t('parAnalyzer.heatmap.sideView.title').value }}</h5>
              <div class="heatmap">
                <div
                  v-for="(row, rowIndex) in heatmapData.sideView"
                  :key="rowIndex"
                  class="heatmap-row"
                >
                  <div
                    v-for="(cell, cellIndex) in row"
                    :key="cellIndex"
                    class="heatmap-cell"
                    :style="{
                      backgroundColor: getHeatmapColor(cell),
                      color: getHeatmapTextColor(cell, heatmapData.maxPar)
                    }"
                  >
                    {{ cell }}
                  </div>
                </div>
              </div>
              <div class="heatmap-labels">
                <div class="label">{{ t('parAnalyzer.heatmap.sideView.depthDistribution').value }} (0-{{ parData.tankDepth }}cm)</div>
              </div>
              </div>
            </div>
          </div>
          
          <div class="heatmap-notes">
            <h5>📝 {{ t('parAnalyzer.heatmap.notes.title').value }}</h5>
            <ul>
              <li>{{ t('parAnalyzer.heatmap.notes.note1').value }}</li>
              <li>{{ t('parAnalyzer.heatmap.notes.note2').value }}</li>
              <li>{{ t('parAnalyzer.heatmap.notes.note3').value }}</li>
              <li>{{ t('parAnalyzer.heatmap.notes.note4').value }}</li>
              <li>{{ t('parAnalyzer.heatmap.notes.note5').value }}</li>
            </ul>
          </div>
        </div>
      </div>
      
      <!-- PAR计算结果 -->
      <div v-if="parResults.show" class="results-section">
        <div class="results-card">
          <div class="results-header">
            <h4>📊 {{ t('parAnalyzer.results.title').value }}</h4>
            <div class="par-badge" :class="getParBadgeClass(parResults.estimatedPar)">
              {{ parResults.estimatedPar }} PAR
            </div>
          </div>
          
          <div class="result-grid">
            <div class="result-item">
              <div class="result-icon">💡</div>
              <div class="result-content">
                <div class="result-label">{{ t('parAnalyzer.results.lightIntensitylable').value }}</div>
                <div class="result-value">{{ parResults.lightIntensity }}</div>
              </div>
            </div>
            <div class="result-item">
              <div class="result-icon">🪸</div>
              <div class="result-content">
                <div class="result-label">{{ t('parAnalyzer.results.suitableCorals').value }}</div>
                <div class="result-value">{{ parResults.suitableCorals.join('、') }}</div>
              </div>
            </div>
            <div class="result-item">
              <div class="result-icon">⚡</div>
              <div class="result-content">
                <div class="result-label">{{ t('parAnalyzer.results.recommendation').value }}</div>
                <div class="result-value">{{ parResults.recommendation }}</div>
              </div>
            </div>
          </div>
          
          <div class="par-comparison">
            <h5>📈 {{ t('parAnalyzer.results.parComparison.title').value }}</h5>
            <div class="comparison-bars">
              <div
                v-for="coral in coralTypes"
                :key="coral.type"
                class="comparison-item"
              >
                <span class="coral-type">{{ coral.name }}</span>
                <div class="comparison-bar">
                  <div
                    class="bar-fill"
                    :class="getComparisonClass(coral.type)"
                    :style="{ width: getComparisonWidth(coral) + '%' }"
                  ></div>
                </div>
                <span class="par-range">{{ coral.minPar }}-{{ coral.maxPar }}</span>
              </div>
            </div>
          </div>
          
          <div class="action-steps">
            <h5>🎯 {{ t('parAnalyzer.results.actionSteps.title').value }}</h5>
            <div class="steps-grid">
              <div v-for="step in parResults.actionSteps" :key="step" class="step-item">
                <div class="step-text">{{ step }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from '../composables/useI18n'

const { t } = useI18n()

// 响应式数据
const activeTab = ref<'calculator' | 'recommendations' | 'chart' | 'heatmap'>('calculator')

// LED品牌数据
const ledBrands = {
  aipai_a8_pro: {
    name: '艾湃 A8 Pro',
    power: 240,
    basePar: 532, // 水面下10cm，灯高15cm，中心点
    parAt30cm: 369, // 水面下30cm，灯高15cm，中心点
    parAt50cm: 240  // 水面下50cm，灯高15cm，中心点
  },
  aipai_a7_3rd: {
    name: '艾湃 A7 三代',
    power: 100,
    basePar: 265, // 水面下10cm，灯高15cm，中心点
    parAt30cm: 120, // 水面下30cm，灯高15cm，中心点
    parAt50cm: 65   // 水面下50cm，灯高15cm，中心点
  },
  kessil_a360x: {
    name: 'Kessil A360X',
    power: 90,
    basePar: 280,
    parAt30cm: 150,
    parAt50cm: 80
  },
  ai_prime_hd: {
    name: 'AI Prime HD',
    power: 55,
    basePar: 180,
    parAt30cm: 90,
    parAt50cm: 45
  },
  radion_xr15: {
    name: 'EcoTech Radion XR15',
    power: 90,
    basePar: 300,
    parAt30cm: 160,
    parAt50cm: 85
  },
  radion_xr30: {
    name: 'EcoTech Radion XR30',
    power: 170,
    basePar: 450,
    parAt30cm: 250,
    parAt50cm: 140
  }
}

// PAR计算数据
const parData = ref({
  lightType: 'led' as 'led' | 't5' | 'metal_halide',
  ledBrand: 'aipai_a7_3rd' as string,
  lightPower: 100,
  lightPowerPercent: 80,
  lightHeight: 15,
  tankDepth: 30,
  measurementDepth: 20,
  actualPar: 0
})

// PAR计算结果
const parResults = ref({
  show: false,
  estimatedPar: 0,
  lightIntensity: '',
  suitableCorals: [] as string[],
  recommendation: '',
  actionSteps: [] as string[]
})

// 计算属性
const canCalculatePAR = computed(() => 
  parData.value.lightPower > 0 && 
  parData.value.lightHeight > 0 && 
  parData.value.tankDepth > 0
)

const parMarks = computed(() => {
  // 固定PAR值刻度：400, 300, 200, 100, 0（从上到下）
  return [400, 300, 200, 100, 0]
})

// 热力图数据
const heatmapData = ref({
  topView: [] as number[][],
  sideView: [] as number[][],
  maxPar: 0
})

const parBars = computed(() => {
  if (!parResults.value.show) {
    return []
  }
  
  const bars: Array<{
    depth: number
    par: number
    height: number
    depthLabel: string
  }> = []
  const maxDepth = parData.value.tankDepth
  
  // 生成5个均匀分布的深度点：0cm, 1/4深度, 1/2深度, 3/4深度, 最大深度
  const depths = [0]
  if (maxDepth > 0) {
    depths.push(Math.round(maxDepth * 0.25))
    depths.push(Math.round(maxDepth * 0.5))
    depths.push(Math.round(maxDepth * 0.75))
    depths.push(maxDepth)
  }
  
  // 使用固定的最大PAR值400作为基准
  const fixedMaxPar = 400
  
  depths.forEach((depth) => {
    const par = calculateParAtDepth(depth)
    
    // 柱状图高度：基于PAR值相对于固定最大PAR值500的比例
    let height = 0
    if (fixedMaxPar > 0) {
      height = (par / fixedMaxPar) * 100
    }
    
    // 确保最小高度为5%，最大高度为100%
    height = Math.max(5, Math.min(height, 100))
    
    bars.push({
      depth,
      par: Math.round(par),
      height: height,
      depthLabel: `${depth}cm`
    })
  })
  return bars
})

// 计算特定深度的PAR值
const calculateParAtDepth = (depth: number): number => {
  // 如果是品牌LED灯具，使用基于实测数据的指数衰减模型
  if (parData.value.lightType === 'led' && parData.value.ledBrand !== 'custom') {
    const brand = ledBrands[parData.value.ledBrand as keyof typeof ledBrands]

    // 精确衰减系数 k，根据你提供的实测数据拟合得出
    const attenuationCoefficients = {
      aipai_a8_pro: 0.0193,   // 拟合 10cm→50cm: 532 → 240
      aipai_a7_3rd: 0.0396,   // 拟合 10cm→30cm: 265 → 120
      kessil_a360x: 0.025,
      ai_prime_hd: 0.030,
      radion_xr15: 0.022,
      radion_xr30: 0.020
    }

    const k = attenuationCoefficients[parData.value.ledBrand as keyof typeof attenuationCoefficients] || 0.025

    // 📌 关键：以 10cm 深度为基准（brand.basePar 的实测条件）
    const refDepth = 10 // cm
    const parAtRef = brand.basePar

    // 使用指数衰减模型：PAR(z) = PAR(10) × exp(-k × (z - 10))
    const deltaDepth = depth - refDepth
    let par = parAtRef * Math.exp(-k * deltaDepth)

    // 应用功率百分比（非线性校正）
    const powerFactor = Math.pow(parData.value.lightPowerPercent / 100, 0.8)
    par *= powerFactor

    // 应用灯具高度调整（平方反比近似，基准高度 15cm）
    const baseHeight = 15
    const heightFactor = Math.pow(baseHeight / Math.max(parData.value.lightHeight, 5), 2)
    par *= heightFactor

    return Math.max(0, Math.round(par))
  }

  // 自定义灯具或其他光源类型：保留原有逻辑（可后续优化）
  let basePar = parData.value.lightPower * 2
  const lightTypeMultipliers = {
    led: 1.2,
    t5: 1.0,
    metal_halide: 1.5
  }
  basePar *= lightTypeMultipliers[parData.value.lightType]

  // 高度衰减（线性近似）
  const heightFactor = Math.max(0.3, 1 - (parData.value.lightHeight - 20) / 100)
  basePar *= heightFactor

  // 深度衰减（线性近似）
  const depthFactor = Math.max(0.1, 1 - depth / 100)
  return basePar * depthFactor
}

// 生成热力图数据
const generateHeatmapData = () => {
  const topView = []
  const sideView = []
  let maxPar = 0
  
  // 生成顶视图热力图数据 (5x5网格)
  const gridSize = 5
  for (let x = 0; x < gridSize; x++) {
    const row = []
    for (let y = 0; y < gridSize; y++) {
      // 改进的中心因子计算，考虑灯具的光束角特性
      const normalizedX = (x - (gridSize - 1) / 2) / ((gridSize - 1) / 2)
      const normalizedY = (y - (gridSize - 1) / 2) / ((gridSize - 1) / 2)
      const distanceFromCenter = Math.sqrt(normalizedX * normalizedX + normalizedY * normalizedY)
      
      // 使用高斯分布模拟光斑分布，更符合实际灯具特性
      const beamAngle = 0.8 // 光束角参数（0-1，越大表示光束越集中）
      const centerFactor = Math.exp(-distanceFromCenter * distanceFromCenter / (2 * beamAngle * beamAngle))
      
      // 确保边缘区域有最小光照
      const minFactor = 0.15
      const adjustedFactor = Math.max(minFactor, centerFactor)
      
      const par = calculateParAtDepth(parData.value.measurementDepth) * adjustedFactor
      row.push(Math.round(par))
      maxPar = Math.max(maxPar, par)
    }
    topView.push(row)
  }
  
  // 生成侧视图热力图数据 (5x5网格，基于整个鱼缸深度)
  const depthSteps = 5
  for (let depthIndex = 0; depthIndex < depthSteps; depthIndex++) {
    const row = []
    // 计算当前深度，从水面(0cm)到缸底(最大深度)
    const depth = Math.round((depthIndex / (depthSteps - 1)) * parData.value.tankDepth)
    for (let x = 0; x < gridSize; x++) {
      // 改进的水平衰减计算
      const normalizedX = (x - (gridSize - 1) / 2) / ((gridSize - 1) / 2)
      const distanceFromCenter = Math.abs(normalizedX)
      
      // 使用高斯分布模拟水平光分布
      const horizontalBeamAngle = 0.6 // 水平光束角
      const horizontalFactor = Math.exp(-distanceFromCenter * distanceFromCenter / (2 * horizontalBeamAngle * horizontalBeamAngle))
      
      // 确保边缘区域有最小光照
      const minHorizontalFactor = 0.2
      const adjustedHorizontalFactor = Math.max(minHorizontalFactor, horizontalFactor)
      
      // 计算该深度和水平位置的PAR值
      const par = calculateParAtDepth(depth) * adjustedHorizontalFactor
      row.push(Math.round(par))
      maxPar = Math.max(maxPar, par)
    }
    sideView.push(row)
  }
  
  heatmapData.value = {
    topView,
    sideView,
    maxPar
  }
}

// 珊瑚类型数据
const coralTypes = computed(() => [
  { type: 'soft', name: t('parAnalyzer.coral.soft.title').value, minPar: 50, maxPar: 150 },
  { type: 'lps', name: t('parAnalyzer.coral.lps.title').value, minPar: 100, maxPar: 250 },
  { type: 'sps', name: t('parAnalyzer.coral.sps.title').value, minPar: 200, maxPar: 400 }
])

// 方法
const onLightTypeChange = () => {
  if (parData.value.lightType !== 'led') {
    parData.value.ledBrand = 'custom'
  }
}

const onLedBrandChange = () => {
  if (parData.value.ledBrand !== 'custom') {
    const brand = ledBrands[parData.value.ledBrand as keyof typeof ledBrands]
    parData.value.lightPower = brand.power
    parData.value.lightPowerPercent = 100
  }
}

const calculatePAR = () => {
  // 使用统一的PAR计算逻辑
  const finalPar = calculateParAtDepth(parData.value.measurementDepth)
  
  // 如果有实际测量值，使用实际值
  const displayPar = parData.value.actualPar > 0 ? parData.value.actualPar : Math.round(finalPar)
  
  // 确定光照强度
  let lightIntensity = ''
  if (displayPar < 50) lightIntensity = t('parAnalyzer.results.lightIntensity.veryLow').value
  else if (displayPar < 100) lightIntensity = t('parAnalyzer.results.lightIntensity.low').value
  else if (displayPar < 200) lightIntensity = t('parAnalyzer.results.lightIntensity.medium').value
  else if (displayPar < 350) lightIntensity = t('parAnalyzer.results.lightIntensity.high').value
  else lightIntensity = t('parAnalyzer.results.lightIntensity.veryHigh').value
  
  // 确定适合的珊瑚类型
  const suitableCorals = []
  if (displayPar >= 50) suitableCorals.push(t('parAnalyzer.coral.soft.title').value)
  if (displayPar >= 100) suitableCorals.push(t('parAnalyzer.coral.lps.title').value)
  if (displayPar >= 200) suitableCorals.push(t('parAnalyzer.coral.sps.title').value)
  
  // 生成建议
  let recommendation = ''
  if (displayPar < 50) {
    recommendation = t('parAnalyzer.results.recommendations.veryLow').value
  } else if (displayPar < 100) {
    recommendation = t('parAnalyzer.results.recommendations.low').value
  } else if (displayPar < 200) {
    recommendation = t('parAnalyzer.results.recommendations.medium').value
  } else if (displayPar < 350) {
    recommendation = t('parAnalyzer.results.recommendations.high').value
  } else {
    recommendation = t('parAnalyzer.results.recommendations.veryHigh').value
  }
  
  // 生成行动步骤
  const actionSteps = [
    t('parAnalyzer.results.actionSteps.step1').value,
    t('parAnalyzer.results.actionSteps.step2').value,
    t('parAnalyzer.results.actionSteps.step3').value,
    t('parAnalyzer.results.actionSteps.step4').value
  ]
  
  // 生成热力图数据
  generateHeatmapData()
  
  // 保存PAR分析数据到localStorage
  const parAnalysisData = {
    timestamp: Date.now(),
    parValue: displayPar,
    lightIntensity,
    suitableCorals,
    recommendation,
    lightType: parData.value.lightType,
    ledBrand: parData.value.ledBrand,
    lightPower: parData.value.lightPower,
    lightPowerPercent: parData.value.lightPowerPercent,
    lightHeight: parData.value.lightHeight,
    measurementDepth: parData.value.measurementDepth,
    tankDepth: parData.value.tankDepth
  }
  
  try {
    localStorage.setItem('parAnalysisData', JSON.stringify(parAnalysisData))
  } catch (error) {
    console.error('保存PAR分析数据失败:', error)
  }
  
  parResults.value = {
    show: true,
    estimatedPar: displayPar,
    lightIntensity,
    suitableCorals,
    recommendation,
    actionSteps
  }
}

// 样式类方法
const getCoralClass = (type: string): string => {
  return `coral-${type}`
}

const getBarClass = (par: number): string => {
  if (par < 100) return 'par-low'
  if (par < 250) return 'par-medium'
  if (par < 400) return 'par-high'
  return 'par-very-high'
}

const getParBadgeClass = (par: number): string => {
  if (par < 100) return 'par-badge-low'
  if (par < 200) return 'par-badge-medium'
  if (par < 350) return 'par-badge-high'
  return 'par-badge-very-high'
}

const getComparisonClass = (type: string): string => {
  return `comparison-${type}`
}

const getComparisonWidth = (coral: any): number => {
  const range = coral.maxPar - coral.minPar
  return (range / 400) * 100
}

// 获取热力图单元格颜色
const getHeatmapColor = (value: number): string => {
  // 使用绝对PAR值而不是相对比例
  if (value < 100) return '#3498db' // 蓝色 - 低PAR (0-100)
  if (value < 200) return '#2ecc71' // 绿色 - 中低PAR (100-200)
  if (value < 300) return '#f39c12' // 橙色 - 中等PAR (200-300)
  return '#e74c3c' // 红色 - 高PAR (300-400)
}

// 获取热力图单元格文本颜色
const getHeatmapTextColor = (value: number, maxValue: number): string => {
  const ratio = value / maxValue
  return ratio < 0.4 ? '#ffffff' : '#000000'
}

// 获取珊瑚区域的样式
const getCoralZoneStyle = (coralType: string): { top: string; height: string } => {
  const coralRanges = {
    sps: { minPar: 200, maxPar: 400 },
    lps: { minPar: 100, maxPar: 250 },
    soft: { minPar: 50, maxPar: 150 },
    deadzone: { minPar: 0, maxPar: 49 }
  }

  const range = coralRanges[coralType as keyof typeof coralRanges]
  if (!range) {
    return { top: '100%', height: '0%' } // 隐藏在底部
  }

  // 基于PAR值计算区域位置（400 PAR在最上面，0 PAR在最下面）
  // 计算顶部位置：基于最小PAR值（400 PAR在最上面）
  const topPercent = ((400 - range.maxPar) / 400) * 100
  // 计算高度：基于PAR值范围
  const heightPercent = ((range.maxPar - range.minPar) / 400) * 100

  // 确保区域精确对齐Y轴刻度
  const adjustedTopPercent = Math.max(0, Math.min(topPercent, 100))
  const adjustedHeightPercent = Math.max(0, Math.min(heightPercent, 100 - adjustedTopPercent))

  return {
    top: `${adjustedTopPercent}%`,
    height: `${adjustedHeightPercent}%`
  }
}


onMounted(() => {
  // 组件挂载时不生成热力图数据，等待用户计算
  // 可选：取消注释以下行来验证计算模型
  // validateCalculationModel()
})
</script>

<style scoped>
/* 组件特定样式 */
.module-header {
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-bottom: none;
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.module-header h2 {
  margin: 0;
  font-size: 1.3em;
  color: white;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
}

.toggle-icon {
  font-size: 0.9em;
  color: rgba(255,255,255,0.8);
  transition: transform 0.3s ease;
}

.toggle-icon:hover {
  transform: scale(1.1);
}

.recommendations-section {
  max-height: 600px;
  overflow-y: auto;
  padding: 0 20px;
}

.recommendations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.recommendation-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-left: 4px solid #3498db;
}

.coral-soft {
  border-left-color: #3498db;
}

.coral-lps {
  border-left-color: #f39c12;
}

.coral-sps {
  border-left-color: #e74c3c;
}


.recommendation-card h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1.2em;
}

.par-range {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.range-label {
  color: #6c757d;
  font-weight: 500;
}

.range-value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.1em;
}

.coral-examples,
.lighting-tips {
  margin-bottom: 15px;
}

.coral-examples p,
.lighting-tips p {
  margin: 0 0 8px 0;
  font-weight: 600;
  color: #495057;
}

.coral-examples ul,
.lighting-tips ul {
  margin: 0;
  padding-left: 20px;
  color: #6c757d;
}

.coral-examples li,
.lighting-tips li {
  margin-bottom: 4px;
  line-height: 1.4;
}

.general-tips {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 0;
}

.general-tips h4 {
  margin: 0 0 20px 0;
  color: #2c3e50;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.tip-item {
  background: white;
  border-radius: 6px;
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.tip-item h5 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 1em;
}

.tip-item p {
  margin: 0;
  color: #6c757d;
  line-height: 1.4;
  font-size: 0.9em;
}

.chart-section {
  max-height: 600px;
  overflow-y: auto;
  padding: 0 20px;
}

.chart-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 15px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.chart-header h4 {
  margin: 0;
  color: #2c3e50;
}

.chart-legend {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9em;
  color: #6c757d;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.legend-color.soft {
  background: #3498db;
}

.legend-color.lps {
  background: #f39c12;
}

.legend-color.sps {
  background: #e74c3c;
}

.depth-chart {
  display: flex;
  height: 400px;
  position: relative;
  border: 0px;
  border-radius: 4px;
  background: #f8f9fa;
}

.par-scale {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 10px;
  border-right: 1px solid #e9ecef;
  background: white;
}

.par-mark {
  display: flex;
  align-items: center;
  gap: 10px;
}

.par-value-label {
  font-size: 0.8em;
  color: #6c757d;
  min-width: 50px;
}

.par-line {
  width: 10px;
  height: 1px;
  background: #6c757d;
}

.par-bars {
  display: flex;
  justify-content: space-around;
  flex: 1;
  padding: 20px 20px 0 20px;
  position: relative;
  align-items: flex-end;
  height: 100%;
}

.par-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
}

.par-bar {
  width: 50px;
  background: #3498db;
  border-radius: 4px 4px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  transition: all 0.3s ease;
  margin: 0 5px;
  min-height: 20px;
}

.depth-label {
  margin-top: 8px;
  font-size: 0.8em;
  color: #6c757d;
  font-weight: 500;
}

.par-low {
  background: #3498db;
}

.par-medium {
  background: #f39c12;
}

.par-high {
  background: #e74c3c;
}

.par-very-high {
  background: #9b59b6;
}

.par-value {
  color: white;
  font-weight: 600;
  font-size: 0.9em;
  margin-bottom: -8px;
}

.par-label {
  color: rgba(255,255,255,0.8);
  font-size: 0.7em;
}

.coral-zones {
  position: absolute;
  top: 20px;
  left: 90px;
  right: 0;
  bottom: 0;
  pointer-events: none;
  height: 360px;
}

.coral-zone {
  position: absolute;
  left: 0;
  right: 0;
  opacity: 0.3;
  display: flex;
  align-items: center;
  padding-left: 60px; /* 增加左边距避免与Y轴重叠 */
}

.coral-zone.deadzone {
  background: #e9ecef;
}

.coral-zone.soft {
  background: #3498db;
}

.coral-zone.lps {
  background: #f39c12;
}

.coral-zone.sps {
  background: #e74c3c;
}

.zone-label {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9em;
  text-shadow: 1px 1px 2px rgba(255,255,255,0.8);
  background: rgba(255,255,255,0.7);
  padding: 2px 6px;
  border-radius: 3px;
}

.chart-notes {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.chart-notes h5 {
  margin: 0 0 15px 0;
  color: #2c3e50;
}

.chart-notes ul {
  margin: 0;
  padding-left: 20px;
  color: #6c757d;
}

.chart-notes li {
  margin-bottom: 8px;
  line-height: 1.4;
}

.par-comparison {
  margin: 20px 0;
}

.comparison-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.comparison-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.coral-type {
  min-width: 80px;
  font-weight: 500;
  color: #2c3e50;
}

.comparison-bar {
  flex: 1;
  height: 20px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.comparison-soft {
  background: #3498db;
}

.comparison-lps {
  background: #f39c12;
}

.comparison-sps {
  background: #e74c3c;
}


.par-range {
  min-width: 80px;
  text-align: right;
  color: #6c757d;
  font-size: 0.9em;
}

/* 热力图样式 */
.heatmap-section {
  max-height: 600px;
  overflow-y: auto;
  padding: 0 20px;
}

.heatmap-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 15px;
}

.heatmap-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.heatmap-header h4 {
  margin: 0;
  color: #2c3e50;
}

.heatmap-legend {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.heatmap-legend .legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8em;
  color: #6c757d;
}

.heatmap-legend .legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.heatmap-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 20px;
}

.heatmap-view {
  text-align: center;
}

.heatmap-view h5 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1em;
}

.heatmap {
  display: inline-block;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.heatmap-row {
  display: flex;
}

.heatmap-cell {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9em;
  font-weight: 700;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

.heatmap-labels {
  margin-top: 10px;
}

.heatmap-labels .label {
  font-size: 0.8em;
  color: #6c757d;
  text-align: center;
}

.heatmap-notes {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.heatmap-notes h5 {
  margin: 0 0 15px 0;
  color: #2c3e50;
}

.heatmap-notes ul {
  margin: 0;
  padding-left: 20px;
  color: #6c757d;
}

.heatmap-notes li {
  margin-bottom: 8px;
  line-height: 1.4;
}

/* PAR分析结果样式 */
.results-section {
  margin-top: 20px;
  padding: 0 20px;
}

.results-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border: 1px solid #e9ecef;
  margin-bottom: 0;
}

.results-header {
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f8f9fa;
}

.results-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2em;
  font-weight: 600;
}

.par-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 1.1em;
  color: white;
  min-width: 80px;
  text-align: center;
}

.par-badge-low {
  background: #3498db;
}

.par-badge-medium {
  background: #f39c12;
}

.par-badge-high {
  background: #e74c3c;
}

.par-badge-very-high {
  background: #9b59b6;
}

.result-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.result-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #3498db;
}

.result-icon {
  font-size: 1.2em;
  margin-top: 2px;
}

.result-content {
  flex: 1;
}

.result-label {
  font-size: 0.85em;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 4px;
}

.result-value {
  font-size: 1em;
  color: #2c3e50;
  font-weight: 600;
  line-height: 1.4;
}

.action-steps {
  margin-top: 25px;
}

.action-steps h5 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 1.1em;
  font-weight: 600;
}

.steps-grid {
  display: grid;
  gap: 12px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #3498db;
}

.step-icon {
  color: #3498db;
  font-weight: 700;
  font-size: 1.1em;
  margin-top: 1px;
  min-width: 20px;
}

.step-text {
  color: #495057;
  line-height: 1.4;
  font-size: 0.95em;
}

/* 无数据提示样式 */
.no-data-message {
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin: 0 20px;
}

.no-data-message p {
  margin: 0 0 20px 0;
  color: #6c757d;
  font-size: 1.1em;
}

.no-data-message .calculate-btn {
  background: #3498db;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.3s ease;
}

.no-data-message .calculate-btn:hover {
  background: #2980b9;
}

/* 灯具类型提示样式 */
.light-type-tips {
  margin-top: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #3498db;
}

.tip-content {
  font-size: 0.85em;
  line-height: 1.4;
  color: #495057;
}

.tip-content strong {
  color: #2c3e50;
}

/* 功率滑块样式 */
.form-control-range {
  width: 100%;
  margin: 10px 0;
}

.power-display {
  text-align: center;
  font-weight: 600;
  color: #2c3e50;
  margin-top: 5px;
}

/* 响应式设计 */
@media (max-width: 768px) { 
  .calculator-tabs{
    margin: 0px 0 15px 0 !important;
  }

  .recommendations-grid {
    grid-template-columns: 1fr;
  }
  
  .tips-grid {
    grid-template-columns: 1fr;
  }
  
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .chart-legend {
    justify-content: flex-start;
  }
  
  .depth-chart {
    height: 300px;
  }
  
  .par-bars {
    padding: 10px;
  }
  
  .par-bar {
    width: 22px;
  }
  
  /* 修复移动端珊瑚区域图标定位 */
  .coral-zones {
    left: 70px; /* 调整左边距适应移动端布局 */
    height: 280px; /* 调整高度适应移动端图表高度 */
    top: 10px; /* 调整顶部位置与移动端图表对齐 */
  }
  
  .coral-zone {
    padding-left: 30px; /* 进一步减少左边距避免与Y轴重叠 */
  }
  
  /* 确保移动端PAR刻度与珊瑚区域精确对齐 */
  .par-scale {
    padding: 10px 5px; /* 进一步减少内边距 */
    min-width: 50px; /* 设置最小宽度 */
  }
  
  .par-value-label {
    font-size: 0.45em; /* 进一步减小字体大小 */
    min-width: 35px; /* 进一步减少最小宽度 */
  }

  .par-value{
    font-size: 0.45em;
    font-weight: normal;
  }
  
  .par-mark {
    gap: 5px; /* 减少刻度标记之间的间距 */
  }
  
  .par-line {
    width: 6px; /* 缩短刻度线长度 */
  }
  
  .comparison-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .coral-type {
    min-width: auto;
  }
  
  .comparison-bar {
    width: 100%;
  }
  
  .heatmap-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .heatmap-cell {
    width: 35px;
    height: 35px;
    font-size: 0.8em;
    font-weight: 700;
  }
  
  .heatmap-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .heatmap-legend {
    justify-content: flex-start;
  }
}
</style>